
import { useNavigate } from "react-router-dom";
import { isSSR } from "@/lib/isSSR";
import { formatCityFromSlug, cityToSlug, normalizeAccents } from "@/utils/cityUtils";
import { debugWarn } from '@/utils/debugLogger';

export function useDealerUtils() {
  // Use navigate only in client-side rendering
  const navigate = !isSSR() ? useNavigate() : null;

  const formatCityName = (city?: string) => {
    if (!city) return '';
    return city
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Questa è la funzione chiave che stiamo modificando
  const createDealerCanonicalPath = (dealerId: string, dealerCity?: string, urlCity?: string) => {
    // Funzione helper per creare uno slug sicuro da una città
    const createSafeSlug = (city: string | undefined | null): string => {
      if (!city || typeof city !== 'string' || city.trim() === '' || city === 'undefined' || city === 'null') {
        return 'roma'; // Fallback sicuro
      }
      
      return normalizeAccents(city)
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/gi, '') // Rimuove caratteri speciali (mantiene trattini)
        .replace(/\s+/g, '-') // Sostituisce spazi con trattini
        .replace(/-+/g, '-') // Sostituisce trattini multipli con uno singolo
        .replace(/^-|-$/g, ''); // Rimuove trattini iniziali e finali
    };

    // 🚨 CRITICAL FIX: Validate dealer ID first to prevent /undefined URLs
    if (!dealerId || dealerId === 'undefined' || dealerId === 'null' || dealerId.trim() === '') {
      debugWarn('general', `Invalid dealer ID: "${dealerId}". Returning city page instead.`, undefined, 'useDealerUtils');
      // Return city page URL instead of invalid dealer URL
      const citySlug = createSafeSlug(dealerCity || urlCity);
      return `/compro-oro/${citySlug}`;
    }
    
    // Additional validation: check if dealerId looks like a valid UUID
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidPattern.test(dealerId)) {
      debugWarn('general', `Invalid dealer ID format: "${dealerId}". Expected UUID format.`, undefined, 'useDealerUtils');
      const citySlug = createSafeSlug(dealerCity || urlCity);
      return `/compro-oro/${citySlug}`;
    }
    
    // Assicuriamoci sempre di avere una città valida nell'URL
    // Priorità: dealerCity -> urlCity -> fallback "roma"
    let citySlug = createSafeSlug(dealerCity);
    
    // Se dealerCity non è valida, prova con urlCity
    if (citySlug === 'roma' && urlCity && urlCity !== 'undefined' && urlCity !== 'null') {
      citySlug = createSafeSlug(urlCity);
    }
    
    // At this point we know dealerId is valid, so we can use it directly
    return `/compro-oro/${citySlug}/${dealerId}`;
  };

  const createCitySlug = (city: string) => {
    return cityToSlug(city);
  };

  const handleGoBack = (citySlug?: string) => {
    if (isSSR() || !navigate) {
      // No-op in server-side rendering or if navigate is not available
      return;
    }
    
    // citySlug è già nel formato corretto per l'URL (es: "santangelo-in-formis")
    if (citySlug) {
      navigate(`/compro-oro/${citySlug}`);
    } else {
      navigate("/compro-oro");
    }
  };

  const convertMetalFormat = (metal: "ORO" | "ARGENTO"): "gold" | "silver" => {
    return metal === "ORO" ? "gold" : "silver";
  };

  const formatCityNameFromSlug = (citySlug: string) => {
    return formatCityFromSlug(citySlug);
  };

  return {
    formatCityName,
    formatCityNameFromSlug,
    formatCityFromSlug,
    createDealerCanonicalPath,
    createCitySlug,
    handleGoBack,
    convertMetalFormat
  };
}
